<!-- 
System: GIGSTIMER
Developer(s): 
Date: 26-04-2021
Organization: Programmer Force
Purpose: Allows visualization of work/task(s) and minimize task updation effort
-->
<template>
  <div style="height: calc(100vh - 46px)">
    <!-- Skeleton Loader Start -->
    <div v-if="getFetchKanbanLoader">
      <v-row class="mt-3 mx-3" v-for="n in 3" :key="n">
        <v-col class="mx-2" v-for="n in 3" :key="n">
          <v-skeleton-loader
            light
            tile
            class="card-skeleton-border rounded"
            loading
            type="list-item-three-line,actions"
          ></v-skeleton-loader>
        </v-col>
      </v-row>
    </div>
    <!-- Kanban Cards -->
    <v-sheet class="overflow-hidden h-100" style="position: relative" v-else>
      <div
        style="overflow-x: scroll"
        class="mt-1 mx-4 d-flex"
        :class="[
          { edited_scrollbar: isMac },
          {
            'default-width': getSidebarMini,
          },
          {
            'reduced-width': !getSidebarMini,
          },
        ]"
      >
        <!-- PENDING TASKS(S) -->
        <div
          :class="[
            { edited_scrollbar: isMac },
            { 'col-width-1': getSidebarMini },
            { 'col-width-2': !getSidebarMini },
            { 'col-width-sm': $vuetify.breakpoint.xs },
          ]"
          class="card-css pending-col-top-border mx-2 my-1 dark-purple-text rounded column-height col-width pa-4"
        >
          <div
            class="dark-purple-text font-weight-bold body-1 sticky d-flex pb-1"
          >
            <span>Pending</span> <v-spacer></v-spacer>
            <v-tooltip
              :disabled="$vuetify.breakpoint.xs"
              content-class="arrow-up"
              color="#DCD8F3"
              bottom
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  class="theme-border"
                  text
                  outlined
                  depressed
                  :height="25"
                  :min-width="6"
                  @click="openAssignTaskDialog = true"
                  ><v-icon color="#757096" size="18">mdi-plus</v-icon></v-btn
                >
              </template>
              <span
                class="font-weight-bold dark-purple-text"
                v-if="getActiveTask"
                >Add a future task</span
              >
              <span class="font-weight-bold dark-purple-text" v-else
                >Assign task</span
              >
            </v-tooltip>
          </div>
          <draggable
            class="kanban-col-height"
            :class="pendingList.length ? ' y-scroll' : ''"
            :list="pendingList"
            group="tasks"
            :move="checkMovePending"
            @change="addPendingTask"
            animation="500"
            id="pending"
          >
            <div
              id="pending-empty-col"
              class="column-height d-flex flex-column align-center justify-center"
              v-if="!pendingList.length"
            >
              <v-icon color="#CBC9E6" x-large>mdi-file-document-outline</v-icon>
              <p class="lighter-purple-text body-2 font-weight-bold">
                No task pending yet
              </p>
            </div>
            <v-card
              v-else
              :ripple="false"
              elevation="0"
              class="pointer my-3 theme-border pa-2"
              v-for="task in pendingList"
              :key="task.task_id"
              @click.stop="editTask(task)"
            >
              <v-card-title class="body-2 dark-purple-text py-0 px-2 d-flex">
                <!-- Labels' Preview -->
                <div class="w-100 mt-0">
                  <span v-for="(label, index) in task.labels" :key="index">
                    <v-chip
                      v-if="
                        getAllLabels.find((item) => {
                          return item.id == label;
                        }) && index < 6
                      "
                      class="mr-1 pointer"
                      label
                      :style="
                        showLabelName
                          ? 'height:20px;border-radius:2px;max-width: 140px;font-size:12px'
                          : 'height:8px;width:40px'
                      "
                      style="max-width: 140px"
                      x-small
                      :color="
                        getAllLabels.find((element) => {
                          return element.id == label;
                        }).hexcode
                      "
                      @click.stop="
                        showLabelName == null
                          ? (showLabelName = task)
                          : (showLabelName = null)
                      "
                    >
                      <span
                        class="text-truncate d-inline-block white--text text-capitalize font-weight-bold"
                        style="max-width: 140px"
                      >
                        {{
                          showLabelName
                            ? getAllLabels.find((element) => {
                                return element.id == label;
                              }).title
                            : ""
                        }}
                      </span>
                    </v-chip>
                  </span>
                  <v-tooltip
                    v-if="task.labels && task.labels.length > 6"
                    content-class="white pa-0 tooltip-opacity"
                    bottom
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" color="#757096" small
                        >mdi-dots-horizontal</v-icon
                      >
                    </template>
                    <span :class="isMac ? 'edited_scrollbar' : ''">
                      <v-card
                        max-width="300px"
                        max-height="200px"
                        class="y-scroll shadow"
                      >
                        <v-card-text>
                          <span
                            v-for="(label, index) in task.labels"
                            :key="index"
                          >
                            <v-chip
                              v-if="
                                getAllLabels.find((item) => {
                                  return item.id == label;
                                })
                              "
                              class="mr-1 mb-1 pointer"
                              label
                              style="'height:20px;border-radius:2px;max-width: 140px;font-size:12px'
                          "
                              x-small
                              :color="
                                getAllLabels.find((element) => {
                                  return element.id == label;
                                }).hexcode
                              "
                            >
                              <span
                                class="text-truncate d-inline-block white--text text-capitalize font-weight-bold"
                                style="max-width: 140px"
                              >
                                {{
                                  getAllLabels.find((element) => {
                                    return element.id == label;
                                  }).title
                                }}
                              </span>
                            </v-chip>
                          </span>
                        </v-card-text>
                      </v-card>
                    </span>
                  </v-tooltip>
                </div>
                <v-chip
                  v-if="task.just_added"
                  style="z-index: 1; position: absolute; top: -8px; width: 80px"
                  class="ml-n1 px-1"
                  x-small
                  color="#ffb74d"
                  label
                  dark
                >
                  <v-avatar x-small class="mx-0 px-0" left>
                    <v-icon x-small>mdi-star</v-icon>
                  </v-avatar>
                  New Task
                </v-chip>
                <div style="max-width: 85%">
                  <!-- @click enable edit -->
                  <v-text-field
                    autocomplete="off"
                    autofocus
                    v-model="taskTitle"
                    dense
                    hide-details=""
                    color="#2a206a"
                    class="body-2 transparent my-0"
                    v-if="
                      selectedTask &&
                      selectedTask.task_id == task.task_id &&
                      task.assigned_by &&
                      (getSelectedCompany.role != 'employee' ||
                        task.assigned_by.id == getLoggedInUser.id)
                    "
                    @click.stop=""
                    @blur="selectedTask = null"
                    @keydown.esc="selectedTask = null"
                    @keydown.enter="editCardTitle(task)"
                  >
                    <template v-slot:append>
                      <v-icon @click.stop="editCardTitle(task)" color="green">
                        mdi-check
                      </v-icon>
                    </template>
                  </v-text-field>
                  <!-- Else show title text -->
                  <span
                    v-else
                    @click.stop="
                      getPendingTimer && getPendingTimer.task_id == task.task_id
                        ? () => {}
                        : setSelectedTask(task),
                        (taskTitle = task.title)
                    "
                    class="pointer text-capitalize hyphenate font-weight-bold"
                  >
                    {{ task.title ? task.title : "No Title" }}
                  </span>
                </div>
                <v-spacer></v-spacer>
                <!-- Assignee(s) -->
                <div class="d-flex align-center subtitle-2">
                  <div v-if="task.members" class="transparent pa-0">
                    <div class="d-flex align-center">
                      <div class="d-flex">
                        <!-- Assignee(s) Avatar -->
                        <ProfileImage
                          v-for="member in task.members.slice(0, 1)"
                          :key="member.email"
                          v-show="member.image ? ':src' : ':username'"
                          :src="member.image"
                          :userProfile="true"
                          inline
                          :username="member.name ? member.name : member.email"
                          :size="30"
                          :circular="true"
                          class="text-light d-inline ml-n3"
                          style="z-index: 2"
                        >
                        </ProfileImage>
                        <ProfileImage
                          v-if="task.members.length > 1"
                          inline
                          :username="`+${task.members.length - 1}`"
                          :size="30"
                          :userProfile="true"
                          :circular="true"
                          style="z-index: 1"
                          class="d-inline ml-n2"
                        >
                        </ProfileImage>
                      </div>
                      <!-- <span v-else>
                        <ProfileImage
                          v-show="getLoggedInUser.image ? ':src' : ':username'"
                          :src="getLoggedInUser.image"
                          :userProfile="true"
                          inline
                          :username="
                            getLoggedInUser.name
                              ? getLoggedInUser.name
                              : getLoggedInUser.email
                          "
                          :size="30"
                          :circular="true"
                          class="text-light d-inline ml-n4"
                        ></ProfileImage>
                      </span> -->
                    </div>
                  </div>
                </div>
              </v-card-title>
              <v-card-text class="dark-purple-text px-2">
                <div class="d-flex align-center">
                  <!-- Project -->
                  <div class="d-flex align-center blue-text text-small">
                    <v-icon
                      small
                      :color="task.project ? '#2B6AD8' : '#9E9E9E'"
                      class="mx-0 pa-0"
                      >mdi-circle-medium</v-icon
                    >
                    <!-- <span v-if="!task.project"> Project</span
                    > -->
                    <span
                      :class="[
                        {
                          'grey--text': !task.project,
                        },
                      ]"
                      class="text-capitalize"
                    >
                      {{
                        task.project
                          ? task.project.name
                            ? task.project.name
                            : "No Project"
                          : "No Project"
                      }}</span
                    >
                  </div>
                  <v-divider vertical class="mx-2 py-2 my-0"></v-divider>
                  <!-- Assigned By -->
                  <div class="d-flex align-center text-small">
                    <img src="@/assets/images/user-icon.svg" alt="user-icon" />
                    <span
                      v-if="task.assigned_by"
                      style="max-width: 110px"
                      :class="[
                        {
                          'text-capitalize':
                            task.assigned_by && task.assigned_by.name,
                        },
                      ]"
                      class="mx-1 d-inline-block text-truncate grey-text"
                      >{{
                        task.assigned_by && task.assigned_by.name
                          ? task.assigned_by.name
                          : task.assigned_by.email
                      }}</span
                    >
                  </div>
                </div>
              </v-card-text>
              <v-card-actions>
                <!-- priority -->
                <v-menu nudge-bottom="25">
                  <template v-slot:activator="{ on }">
                    <div
                      v-on="on"
                      :id="`priority-${task.task_id}`"
                      :class="
                        task.assigned_by.id != getLoggedInUser.id &&
                        getSelectedCompany.role == 'employee'
                          ? ''
                          : 'pointer'
                      "
                    >
                      <v-icon color="red" v-if="task.priority == 'high'"
                        >mdi-chevron-triple-up</v-icon
                      >
                      <v-icon
                        color="orange"
                        v-else-if="task.priority == 'medium'"
                        >mdi-chevron-double-up</v-icon
                      >
                      <v-icon color="blue" v-else-if="task.priority == 'low'"
                        >mdi-chevron-double-up</v-icon
                      >

                      <v-icon v-else>mdi-chevron-double-up</v-icon>
                    </div>
                  </template>
                  <div
                    v-if="
                      !(
                        task.assigned_by.id != getLoggedInUser.id &&
                        getSelectedCompany.role == 'employee'
                      )
                    "
                    class="bg-white pointer py-2"
                  >
                    <div
                      v-for="(priority, i) in ['low', 'medium', 'high']"
                      :key="i"
                      :class="
                        task.priority && task.priority == priority
                          ? 'bg-selected'
                          : ''
                      "
                      class="body-2 dark-purple-text text-capitalize px-2 py-1"
                      @click="setPriority(task, priority)"
                    >
                      {{ priority }}
                    </div>
                  </div>
                </v-menu>
                <v-tooltip
                  :disabled="$vuetify.breakpoint.xs"
                  :activator="`#priority-${task.task_id}`"
                  content-class="arrow-up"
                  open-delay="120"
                  bottom
                  color="#DCD8F3"
                >
                  <span
                    class="text-capitalize font-weight-bold dark-purple-text"
                    >{{
                      task.priority ? task.priority + " priority" : "Priority"
                    }}</span
                  >
                </v-tooltip>

                <!-- due date -->
                <v-chip
                  v-if="task.due_date"
                  class="due_date-chip-kb d-flex justify-center align-center"
                  label
                  small
                  light
                  :color="
                    task.due_date === getCurrentDate()
                      ? 'yellow-chip-color'
                      : task.due_date < getCurrentDate()
                      ? 'red-chip-color'
                      : 'green-chip-color'
                  "
                >
                  <span v-if="task.due_date === getCurrentDate()"> Today </span>
                  <span
                    v-else-if="
                      task.due_date === getTommorrowDate(task.due_date)
                    "
                  >
                    Tomorrow
                  </span>
                  <span v-else>
                    {{ getDueDate(task.due_date) }}
                  </span>
                </v-chip>
                <v-chip
                  v-if="!task.due_date"
                  class="due_date-chip-kb d-flex justify-center align-center no-date"
                  color="grey lighten-3"
                  label
                  small
                  light
                >
                  <span>No Due Date</span>
                </v-chip>

                <!-- Recurring -->
                <v-menu nudge-bottom="25">
                  <template v-slot:activator="{ on }">
                    <div
                      v-on="on"
                      :id="`repeat-${task.task_id}`"
                      :class="
                        task.assigned_by.id != getLoggedInUser.id &&
                        getSelectedCompany.role == 'employee'
                          ? ''
                          : 'pointer'
                      "
                    >
                      <v-icon
                        color="green"
                        class="ml-2"
                        size="16"
                        v-show="task.recurring_type"
                        >mdi-cached
                      </v-icon>
                    </div>
                  </template>
                  <div
                    v-if="
                      !(
                        task.assigned_by.id != getLoggedInUser.id &&
                        getSelectedCompany.role == 'employee'
                      )
                    "
                    class="bg-white pointer py-2"
                  >
                    <div
                      v-for="(recurring_type, i) in [
                        'daily',
                        'weekly',
                        'monthly',
                      ]"
                      :key="i"
                      :class="
                        task.recurring_type &&
                        task.recurring_type == recurring_type
                          ? 'bg-selected'
                          : ''
                      "
                      class="body-2 dark-purple-text text-capitalize px-2 py-1"
                      @click="
                        repeatTask(
                          task,
                          recurring_type == task.recurring_type
                            ? null
                            : recurring_type
                        )
                      "
                    >
                      {{ recurring_type }}
                    </div>
                  </div>
                </v-menu>
                <v-tooltip
                  :disabled="$vuetify.breakpoint.xs"
                  :activator="`#repeat-${task.task_id}`"
                  content-class="arrow-up"
                  open-delay="120"
                  bottom
                  color="#DCD8F3"
                >
                  <span
                    class="text-capitalize font-weight-bold dark-purple-text"
                    >{{
                      task.recurring_type
                        ? "Repeat " + task.recurring_type
                        : "Repeat task"
                    }}</span
                  >
                </v-tooltip>

                <!-- Time Spent -->
                <div class="caption d-flex align-center mx-2">
                  <v-icon color="#757096" small>mdi-clock-outline</v-icon>
                  <span
                    class="mx-1 text-capitalize font-weight-bold dusty-purple-text"
                  >
                    {{ getTotalDuration(task) }}</span
                  >
                </div>
                <v-spacer></v-spacer>
                <!-- Flag Icon -->
                <!-- Unflagged -->
                <v-icon
                  v-show="!task.is_flagged"
                  class="pointer"
                  color="#2a206a"
                  size="25"
                  @click.stop="
                    getSelectedCompany.role == 'employee' &&
                    task.assigned_by.id != getLoggedInUser.id
                      ? () => {}
                      : flagTaskKanban(task, true)
                  "
                  >mdi-bookmark-outline</v-icon
                >
                <!-- Flagged -->
                <v-icon
                  v-show="task.is_flagged"
                  @click.stop="
                    getSelectedCompany.role == 'employee' &&
                    task.assigned_by.id != getLoggedInUser.id
                      ? openSnackbar(true, 'Only assigner can flag task')
                      : flagTaskKanban(task, false)
                  "
                  class="pointer"
                  color="#D52A5D"
                  size="25"
                  >mdi-bookmark</v-icon
                >
                <!-- Unflagged -->
                <!-- <v-btn
                  v-show="!task.is_flagged"
                  @click.stop="
                    getSelectedCompany.role == 'employee' &&
                    task.assigned_by.id != getLoggedInUser.id
                      ? () => {}
                      : flagTaskKanban(task, true)
                  "
                  color="#EDEBF9"
                  fab
                  depressed
                  height="30px"
                  width="30px"
                >
                  <v-icon size="20" color="#2a206a"
                    >mdi-bookmark-outline</v-icon
                  >
                </v-btn> -->
                <!-- Flagged -->
                <!-- <v-btn
                  v-show="task.is_flagged"
                  @click.stop="
                    getSelectedCompany.role == 'employee' &&
                    task.assigned_by.id != getLoggedInUser.id
                      ? openSnackbar(true, 'Only assigner can flag task')
                      : flagTaskKanban(task, false)
                  "
                  color="#EDEBF9"
                  fab
                  depressed
                  height="30px"
                  width="30px"
                >
                  <v-icon size="20" color="#D52A5D">mdi-bookmark</v-icon>
                </v-btn> -->

                <!-- comment icon -->
                <v-badge
                  :content="
                    parseInt(task.comments_count) < 100
                      ? task.comments_count
                      : '99+'
                  "
                  v-if="task.comments_count"
                  color="green"
                  :class="[
                    { 'mr-5': parseInt(task.comments_count) > 100 },
                    { 'mr-3': parseInt(task.comments_count) > 9 },
                    {
                      'style-badge': task.assigned_by.id == getLoggedInUser.id,
                    },
                  ]"
                  overlap
                  class="mx-3 mt-1"
                >
                  <img
                    :id="'comment-' + task.task_id"
                    @click.stop="
                      getPendingTimer && getPendingTimer.task_id == task.task_id
                        ? () => {}
                        : openComments(task)
                    "
                    height="26px"
                    width="28px"
                    class="dark-purple-text pointer"
                    :src="comment_icon"
                    alt="comment icon"
                  />
                </v-badge>
                <img
                  v-else
                  @click.stop="
                    getPendingTimer && getPendingTimer.task_id == task.task_id
                      ? () => {}
                      : openComments(task)
                  "
                  height="26px"
                  width="26px"
                  class="dark-purple-text pointer mx-2"
                  :src="comment_icon"
                  alt="comment icon"
                />
                <!-- Play/Pause Button -->
                <div class="d-flex align-center">
                  <img
                    v-show="
                      task.members.find((item) => {
                        return item.id == getLoggedInUser.id;
                      })
                    "
                    @click.stop="
                      getPendingTimer && getPendingTimer.task_id == task.task_id
                        ? () => {}
                        : playTask(task),
                        updatePendingList(task)
                    "
                    src="@/assets/images/play-circle-kanban.svg"
                    alt="play-icon"
                    height="28px"
                    width="28px"
                  />
                </div>
              </v-card-actions>
              <div>
                <v-tooltip
                  :disabled="$vuetify.breakpoint.xs"
                  color="#DCD8F3"
                  content-class="arrow-up"
                  bottom
                >
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      v-on="on"
                      v-bind="attrs"
                      class="serial mx-3 pointer"
                      v-if="task.sr_no"
                      @click.stop="copySerialNumber(task.sr_no)"
                    >
                      #{{ task.sr_no }}
                    </span>
                  </template>
                  <span
                    class="font-weight-bold dark-purple-text"
                    v-if="copiedSerialNumber != task.sr_no"
                    >Copy to clipboard</span
                  >
                  <span
                    class="font-weight-bold dark-purple-text"
                    v-else-if="copiedSerialNumber == task.sr_no"
                    >Copied</span
                  >
                </v-tooltip>
              </div>
            </v-card>
          </draggable>
          <div class="d-flex align-center justify-center pa-2">
            <p
              v-if="
                getPendingPagination.last_page !=
                  getPendingPagination.current_page &&
                getPendingPagination.total > 10
              "
              dark
              class="text-capitalize text-center pointer dusty-purple-text body-2 font-weight-bold"
              @click="loadMorePendingTasks"
            >
              Load More
            </p>
            <v-progress-circular
              v-if="loading == 'loadPending'"
              :size="20"
              width="2"
              color="#757096"
              indeterminate
            ></v-progress-circular>
          </div>
        </div>
        <!-- ACTIVE TASK(S) -->
        <div
          :class="[
            { edited_scrollbar: isMac },
            { 'col-width-1': getSidebarMini },
            { 'col-width-2': !getSidebarMini },
            { 'col-width-sm': $vuetify.breakpoint.xs },
          ]"
          class="card-css active-col-top-border mx-2 my-1 dark-purple-text rounded column-height col-width pa-4"
        >
          <div
            class="dark-purple-text d-flex font-weight-bold body-1 sticky pb-1"
          >
            <span> Active</span> <v-spacer></v-spacer>
            <v-tooltip
              :disabled="$vuetify.breakpoint.xs"
              content-class="arrow-up"
              color="#DCD8F3"
              bottom
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  class="theme-border"
                  text
                  outlined
                  depressed
                  :height="25"
                  :min-width="6"
                  @click="
                    (selectedAddTaskStatus = 'active'),
                      (openAddNewTaskKanbanDialog = true)
                  "
                  ><v-icon color="#757096" size="18">mdi-plus</v-icon></v-btn
                >
              </template>
              <span class="font-weight-bold dark-purple-text">Add Task</span>
            </v-tooltip>
          </div>
          <draggable
            class="kanban-col-height"
            :class="active.length ? ' y-scroll' : ''"
            :list="active"
            group="tasks"
            :move="checkMove"
            id="active"
            animation="500"
            @change="addActiveTask"
          >
            <div
              id="active-empty-col"
              class="column-height d-flex flex-column align-center justify-center"
              v-if="!sortActiveTasks.length"
            >
              <v-icon color="#CBC9E6" x-large>mdi-file-document-outline</v-icon>
              <p class="lighter-purple-text body-2 font-weight-bold">
                No task active yet
              </p>
            </div>
            <v-card
              v-else
              elevation="0"
              :ripple="false"
              class="pointer my-3 theme-border pa-2"
              v-for="task in sortActiveTasks"
              :key="task.name"
              @click.stop="editTask(task)"
            >
              <v-card-title class="body-2 dark-purple-text py-0 px-2">
                <!-- Labels' Preview -->
                <div class="w-100 mt-0">
                  <span v-for="(label, index) in task.labels" :key="index">
                    <v-chip
                      v-if="
                        getAllLabels.find((item) => {
                          return item.id == label;
                        }) && index < 6
                      "
                      class="mr-1 pointer"
                      label
                      :style="
                        showLabelName
                          ? 'height:20px;border-radius:2px;max-width: 140px;font-size:12px'
                          : 'height:8px;width:40px'
                      "
                      style="max-width: 140px"
                      x-small
                      :color="
                        getAllLabels.find((element) => {
                          return element.id == label;
                        }).hexcode
                      "
                      @click.stop="
                        showLabelName == null
                          ? (showLabelName = task)
                          : (showLabelName = null)
                      "
                    >
                      <span
                        class="text-truncate d-inline-block white--text text-capitalize font-weight-bold"
                        style="max-width: 140px"
                      >
                        {{
                          showLabelName
                            ? getAllLabels.find((element) => {
                                return element.id == label;
                              }).title
                            : ""
                        }}
                      </span>
                    </v-chip>
                  </span>
                  <v-tooltip
                    v-if="task.labels && task.labels.length > 6"
                    content-class="white pa-0 tooltip-opacity"
                    bottom
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" color="#757096" small
                        >mdi-dots-horizontal</v-icon
                      >
                    </template>
                    <span :class="isMac ? 'edited_scrollbar' : ''">
                      <v-card
                        max-width="300px"
                        max-height="200px"
                        class="y-scroll shadow"
                      >
                        <v-card-text>
                          <span
                            v-for="(label, index) in task.labels"
                            :key="index"
                          >
                            <v-chip
                              v-if="
                                getAllLabels.find((item) => {
                                  return item.id == label;
                                })
                              "
                              class="mr-1 mb-1 pointer"
                              label
                              style="'height:20px;border-radius:2px;max-width: 140px;font-size:12px'
                              "
                              x-small
                              :color="
                                getAllLabels.find((element) => {
                                  return element.id == label;
                                }).hexcode
                              "
                            >
                              <span
                                class="text-truncate d-inline-block white--text text-capitalize font-weight-bold"
                                style="max-width: 140px"
                              >
                                {{
                                  getAllLabels.find((element) => {
                                    return element.id == label;
                                  }).title
                                }}
                              </span>
                            </v-chip>
                          </span>
                        </v-card-text>
                      </v-card>
                    </span>
                  </v-tooltip>
                </div>
                <v-chip
                  v-if="task.just_dargged || task.just_added"
                  style="z-index: 1; position: absolute; top: -8px; width: 80px"
                  class="ml-n1 px-1"
                  x-small
                  color="#65b6f7"
                  label
                  dark
                >
                  <v-avatar x-small class="mx-0 px-0" left>
                    <v-icon x-small>mdi-star</v-icon>
                  </v-avatar>

                  {{ task.just_dargged ? "Active" : "New Task" }}
                </v-chip>
                <div style="max-width: 85%">
                  <!-- @click enable edit -->
                  <v-text-field
                    autocomplete="off"
                    autofocus
                    v-model="taskTitle"
                    dense
                    hide-details=""
                    color="#2a206a"
                    class="body-2 transparent my-0"
                    v-if="
                      selectedTask &&
                      selectedTask.task_id == task.task_id &&
                      (getSelectedCompany.role != 'employee' ||
                        task.assigned_by.id == getLoggedInUser.id)
                    "
                    @click.stop=""
                    @blur="selectedTask = null"
                    @keydown.esc="selectedTask = null"
                    @keydown.enter="editCardTitle(task)"
                  >
                    <template v-slot:append>
                      <v-icon @click.stop="editCardTitle(task)" color="green">
                        mdi-check
                      </v-icon>
                    </template>
                  </v-text-field>
                  <!-- Else show title text -->
                  <span
                    v-else
                    @click.stop="
                      setSelectedTask(task);
                      taskTitle = task.title;
                    "
                    class="pointer text-capitalize hyphenate font-weight-bold"
                    :class="task.title ? '' : 'font-italic grey--text'"
                  >
                    {{ task.title ? task.title : "No title" }}
                  </span>
                </div>
                <v-spacer></v-spacer>
                <!-- Assignee(s) -->
                <div class="d-flex align-center subtitle-2">
                  <div v-if="task.members" class="transparent pa-0">
                    <div class="d-flex align-center">
                      <div class="d-flex">
                        <!-- Assignee(s) Avatar -->
                        <ProfileImage
                          v-for="member in task.members.slice(0, 1)"
                          :key="member.email"
                          v-show="member.image ? ':src' : ':username'"
                          :src="member.image"
                          :userProfile="true"
                          inline
                          :username="member.name ? member.name : member.email"
                          :size="30"
                          :circular="true"
                          class="text-light d-inline ml-n3"
                          style="z-index: 2"
                        >
                        </ProfileImage>
                        <ProfileImage
                          v-if="task.members.length > 1"
                          inline
                          :username="`+${task.members.length - 1}`"
                          :size="30"
                          :userProfile="true"
                          :circular="true"
                          style="z-index: 1"
                          class="d-inline ml-n2"
                        >
                        </ProfileImage>
                      </div>
                    </div>
                  </div>
                </div>
              </v-card-title>
              <v-card-text class="dark-purple-text px-2">
                <div class="d-flex align-center">
                  <!-- Project -->
                  <div class="d-flex align-center blue-text text-small">
                    <v-icon
                      small
                      :color="task.project ? '#2B6AD8' : '#9E9E9E'"
                      class="mx-0 pa-0"
                      >mdi-circle-medium</v-icon
                    >
                    <!-- <span> Project </span
                    > -->
                    <span
                      :class="[
                        {
                          'grey--text': !task.project,
                        },
                      ]"
                      class="text-capitalize"
                    >
                      {{
                        task.project
                          ? task.project.name
                            ? task.project.name
                            : "No Project"
                          : "No Project"
                      }}</span
                    >
                  </div>
                  <v-divider vertical class="mx-2 py-2 my-0"></v-divider>
                  <!-- Assigned By -->
                  <div class="d-flex align-center text-small">
                    <img src="@/assets/images/user-icon.svg" alt="user-icon" />
                    <span
                      v-if="task.assigned_by"
                      style="max-width: 110px"
                      :class="[
                        {
                          'text-capitalize':
                            task.assigned_by && task.assigned_by.name,
                        },
                      ]"
                      class="mx-1 d-inline-block text-truncate grey-text"
                      >{{
                        task.assigned_by && task.assigned_by.name
                          ? task.assigned_by.name
                          : task.assigned_by.email
                      }}</span
                    >
                  </div>
                </div>
              </v-card-text>
              <v-card-actions
                class="dark-purple-text body-2 d-flex align-center"
              >
                <!-- priority -->
                <v-menu nudge-bottom="25">
                  <template v-slot:activator="{ on }">
                    <div
                      v-on="on"
                      :id="`priority-${task.task_id}`"
                      :class="
                        task.assigned_by.id != getLoggedInUser.id &&
                        getSelectedCompany.role == 'employee'
                          ? ''
                          : 'pointer'
                      "
                    >
                      <v-icon color="red" v-if="task.priority == 'high'"
                        >mdi-chevron-triple-up</v-icon
                      >
                      <v-icon
                        color="orange"
                        v-else-if="task.priority == 'medium'"
                        >mdi-chevron-double-up</v-icon
                      >
                      <v-icon color="blue" v-else-if="task.priority == 'low'"
                        >mdi-chevron-double-up</v-icon
                      >

                      <v-icon v-else>mdi-chevron-double-up</v-icon>
                    </div>
                  </template>
                  <div
                    v-if="
                      !(
                        task.assigned_by.id != getLoggedInUser.id &&
                        getSelectedCompany.role == 'employee'
                      )
                    "
                    class="bg-white pointer py-2"
                  >
                    <div
                      v-for="(priority, i) in ['low', 'medium', 'high']"
                      :key="i"
                      :class="
                        task.priority && task.priority == priority
                          ? 'bg-selected'
                          : ''
                      "
                      class="body-2 dark-purple-text text-capitalize px-2 py-1"
                      @click="setPriority(task, priority)"
                    >
                      {{ priority }}
                    </div>
                  </div>
                </v-menu>
                <v-tooltip
                  :disabled="$vuetify.breakpoint.xs"
                  :activator="`#priority-${task.task_id}`"
                  content-class="arrow-up"
                  open-delay="120"
                  bottom
                  color="#DCD8F3"
                >
                  <span
                    class="text-capitalize font-weight-bold dark-purple-text"
                    >{{
                      task.priority ? task.priority + " priority" : "Priority"
                    }}</span
                  >
                </v-tooltip>
                <!-- Due Date -->
                <v-chip
                  v-if="task.due_date"
                  class="due_date-chip-kb d-flex justify-center align-center"
                  label
                  small
                  light
                  :color="
                    task.due_date === getCurrentDate()
                      ? 'yellow-chip-color'
                      : task.due_date < getCurrentDate()
                      ? 'red-chip-color'
                      : 'green-chip-color'
                  "
                >
                  <span v-if="task.due_date === getCurrentDate()"> Today </span>
                  <span
                    v-else-if="
                      task.due_date === getTommorrowDate(task.due_date)
                    "
                  >
                    Tomorrow
                  </span>
                  <span v-else>
                    {{ getDueDate(task.due_date) }}
                  </span>
                </v-chip>
                <v-chip
                  v-if="!task.due_date"
                  class="due_date-chip-kb d-flex justify-center no-date align-center"
                  color="grey lighten-3"
                  label
                  small
                  light
                >
                  <span>No Due Date</span>
                </v-chip>

                <!-- Recurring -->
                <v-menu nudge-bottom="25">
                  <template v-slot:activator="{ on }">
                    <div
                      v-on="on"
                      :id="`repeat-${task.task_id}`"
                      :class="
                        task.assigned_by.id != getLoggedInUser.id &&
                        getSelectedCompany.role == 'employee'
                          ? ''
                          : 'pointer'
                      "
                    >
                      <v-icon
                        color="green"
                        class="ml-2"
                        size="16"
                        v-show="task.recurring_type"
                        >mdi-cached
                      </v-icon>
                    </div>
                  </template>
                  <div
                    v-if="
                      !(
                        task.assigned_by.id != getLoggedInUser.id &&
                        getSelectedCompany.role == 'employee'
                      )
                    "
                    class="bg-white pointer py-2"
                  >
                    <div
                      v-for="(recurring_type, i) in [
                        'daily',
                        'weekly',
                        'monthly',
                      ]"
                      :key="i"
                      :class="
                        task.recurring_type &&
                        task.recurring_type == recurring_type
                          ? 'bg-selected'
                          : ''
                      "
                      class="body-2 dark-purple-text text-capitalize px-2 py-1"
                      @click="
                        repeatTask(
                          task,
                          recurring_type == task.recurring_type
                            ? null
                            : recurring_type
                        )
                      "
                    >
                      {{ recurring_type }}
                    </div>
                  </div>
                </v-menu>
                <v-tooltip
                  :disabled="$vuetify.breakpoint.xs"
                  :activator="`#repeat-${task.task_id}`"
                  content-class="arrow-up"
                  open-delay="120"
                  bottom
                  color="#DCD8F3"
                >
                  <span
                    class="text-capitalize font-weight-bold dark-purple-text"
                    >{{
                      task.recurring_type
                        ? "Repeat " + task.recurring_type
                        : "Repeat task"
                    }}</span
                  >
                </v-tooltip>

                <!-- Time Spent -->
                <div class="caption d-flex align-center mx-2">
                  <v-icon
                    :color="
                      getActiveTask && getActiveTask.task_id == task.task_id
                        ? '#D52A5D'
                        : '#757096'
                    "
                    small
                    >mdi-clock-outline</v-icon
                  >
                  <span
                    :class="
                      getActiveTask && getActiveTask.task_id == task.task_id
                        ? ' dark-red-text'
                        : 'dusty-purple-text'
                    "
                    class="mx-1 text-capitalize font-weight-bold"
                  >
                    {{
                      getActiveTask && getActiveTask.task_id == task.task_id
                        ? getMyDuration(task)
                        : getTotalDuration(task)
                    }}</span
                  >
                </div>
                <v-spacer></v-spacer>
                <!-- Unflagged -->
                <!-- <v-btn
                  v-show="!task.is_flagged"
                  @click.stop="
                    getSelectedCompany.role == 'employee' &&
                    task.assigned_by.id != getLoggedInUser.id
                      ? () => {}
                      : flagTaskKanban(task, true)
                  "
                  color="#EDEBF9"
                  fab
                  depressed
                  height="30px"
                  width="30px"
                > -->
                <v-icon
                  v-show="!task.is_flagged"
                  class="pointer"
                  color="#2a206a"
                  size="25"
                  @click.stop="
                    getSelectedCompany.role == 'employee' &&
                    task.assigned_by.id != getLoggedInUser.id
                      ? () => {}
                      : flagTaskKanban(task, true)
                  "
                  >mdi-bookmark-outline</v-icon
                >
                <v-icon
                  v-show="task.is_flagged"
                  @click.stop="
                    getSelectedCompany.role == 'employee' &&
                    task.assigned_by.id != getLoggedInUser.id
                      ? openSnackbar(true, 'Only assigner can flag task')
                      : flagTaskKanban(task, false)
                  "
                  class="pointer"
                  color="#D52A5D"
                  size="25"
                  >mdi-bookmark</v-icon
                >
                <!-- </v-btn> -->
                <!-- Flagged -->
                <!-- <v-btn
                  v-show="task.is_flagged"
                  @click.stop="
                    getSelectedCompany.role == 'employee' &&
                    task.assigned_by.id != getLoggedInUser.id
                      ? openSnackbar(true, 'Only assigner can flag task')
                      : flagTaskKanban(task, false)
                  "
                  color="#EDEBF9"
                  fab
                  depressed
                  height="30px"
                  width="30px"
                > -->
                <!-- <v-icon size="20" color="#D52A5D">mdi-bookmark</v-icon> -->
                <!-- </v-btn> -->
                <!-- comment icon -->
                <v-badge
                  :content="
                    parseInt(task.comments_count) < 100
                      ? task.comments_count
                      : '99+'
                  "
                  v-if="task.comments_count"
                  color="green"
                  :class="[
                    { 'mr-5': parseInt(task.comments_count) > 100 },
                    { 'mr-3': parseInt(task.comments_count) > 9 },
                    {
                      'style-badge': task.assigned_by.id == getLoggedInUser.id,
                    },
                  ]"
                  overlap
                  class="mx-2 mt-1"
                >
                  <img
                    :id="'comment-' + task.task_id"
                    @click.stop="
                      getPendingTimer && getPendingTimer.task_id == task.task_id
                        ? () => {}
                        : openComments(task)
                    "
                    height="26px"
                    width="28px"
                    class="dark-purple-text pointer"
                    :src="comment_icon"
                    alt="comment icon"
                  />
                </v-badge>
                <img
                  v-else
                  @click.stop="
                    getPendingTimer && getPendingTimer.task_id == task.task_id
                      ? () => {}
                      : openComments(task)
                  "
                  height="26px"
                  width="26px"
                  class="dark-purple-text pointer mx-2"
                  :src="comment_icon"
                  alt="comment icon"
                />
                <!-- Play/Pause -->
                <div
                  v-if="
                    task.members.find((item) => {
                      return item.id == getLoggedInUser.id;
                    })
                  "
                  class="d-flex align-center"
                >
                  <img
                    v-if="
                      task.members.find((item) => {
                        return item.id == getLoggedInUser.id;
                      }) &&
                      getActiveTask &&
                      getActiveTask.task_id == task.task_id
                    "
                    @click.stop="stopTask()"
                    src="@/assets/images/pause-icon-kanban.svg"
                    alt="play-icon"
                    height="28px"
                    width="28px"
                  />
                  <img
                    v-else
                    @click.stop="playTask(task)"
                    src="@/assets/images/play-circle-kanban.svg"
                    alt="play-icon"
                    height="28px"
                    width="28px"
                  />
                </div>
              </v-card-actions>
              <div>
                <v-tooltip
                  :disabled="$vuetify.breakpoint.xs"
                  color="#DCD8F3"
                  content-class="arrow-up"
                  bottom
                >
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      v-on="on"
                      v-bind="attrs"
                      class="serial mx-3 pointer"
                      v-if="task.sr_no"
                      @click.stop="copySerialNumber(task.sr_no)"
                    >
                      #{{ task.sr_no }}
                    </span>
                  </template>
                  <span
                    class="font-weight-bold dark-purple-text"
                    v-if="copiedSerialNumber != task.sr_no"
                    >Copy to clipboard</span
                  >
                  <span
                    class="font-weight-bold dark-purple-text"
                    v-else-if="copiedSerialNumber == task.sr_no"
                    >Copied</span
                  >
                </v-tooltip>
              </div>
            </v-card>
          </draggable>
          <div class="d-flex align-center justify-center pa-2">
            <p
              v-if="
                getActivePagination.last_page !=
                  getActivePagination.current_page &&
                getActivePagination.total > 10
              "
              class="text-capitalize text-center pointer dusty-purple-text body-2 font-weight-bold"
              @click="loadMoreActiveTasks"
            >
              Load More
            </p>
            <v-progress-circular
              v-if="loading == 'loadActive'"
              color="#757096"
              :size="20"
              width="2"
              indeterminate
            ></v-progress-circular>
          </div>
        </div>
        <!-- COMPLETED TASK(S) -->
        <div
          :class="[
            { edited_scrollbar: isMac },
            { 'col-width-1': getSidebarMini },
            { 'col-width-2': !getSidebarMini },
            { 'col-width-sm': $vuetify.breakpoint.xs },
          ]"
          class="card-css completed-col-top-border mx-2 my-1 dark-purple-text rounded column-height col-width pa-4"
        >
          <div
            class="dark-purple-text d-flex font-weight-bold body-1 sticky pb-1"
          >
            <span> Completed</span> <v-spacer></v-spacer>
            <v-tooltip
              :disabled="$vuetify.breakpoint.xs"
              content-class="arrow-up"
              color="#DCD8F3"
              bottom
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  class="theme-border"
                  text
                  outlined
                  depressed
                  :height="25"
                  :min-width="6"
                  @click="
                    (selectedAddTaskStatus = 'completed'),
                      (openAddNewTaskKanbanDialog = true)
                  "
                  ><v-icon color="#757096" size="18">mdi-plus</v-icon></v-btn
                >
              </template>
              <span class="font-weight-bold dark-purple-text">Add Task</span>
            </v-tooltip>
          </div>
          <draggable
            class="kanban-col-height"
            :class="completed.length ? ' y-scroll' : ''"
            :list="completed"
            group="tasks"
            :move="checkMoveCompleted"
            id="completed"
            animation="500"
            @change="addCompletedTask"
          >
            <div
              id="completed-empty-col"
              class="column-height d-flex flex-column align-center justify-center"
              v-if="!completed.length"
            >
              <v-icon color="#CBC9E6" x-large>mdi-file-document-outline</v-icon>
              <p class="lighter-purple-text body-2 font-weight-bold">
                No task completed yet
              </p>
            </div>

            <v-card
              v-else
              elevation="0"
              :ripple="false"
              class="pointer my-3 theme-border pa-2"
              v-for="task in completed"
              :key="task.name"
              @click.stop="editTask(task)"
            >
              <v-card-title class="body-2 dark-purple-text py-0 px-2 d-flex">
                <!-- Labels' Preview -->
                <div class="w-100 mt-0">
                  <span v-for="(label, index) in task.labels" :key="index">
                    <v-chip
                      v-if="
                        getAllLabels.find((item) => {
                          return item.id == label;
                        }) && index < 6
                      "
                      class="mr-1 pointer"
                      label
                      :style="
                        showLabelName
                          ? 'height:20px;border-radius:2px;max-width: 140px;font-size:12px'
                          : 'height:8px;width:40px'
                      "
                      style="max-width: 140px"
                      x-small
                      :color="
                        getAllLabels.find((element) => {
                          return element.id == label;
                        }).hexcode
                      "
                      @click.stop="
                        showLabelName == null
                          ? (showLabelName = task)
                          : (showLabelName = null)
                      "
                    >
                      <span
                        class="text-truncate d-inline-block white--text text-capitalize font-weight-bold"
                        style="max-width: 140px"
                      >
                        {{
                          showLabelName
                            ? getAllLabels.find((element) => {
                                return element.id == label;
                              }).title
                            : ""
                        }}
                      </span>
                    </v-chip>
                  </span>
                  <v-tooltip
                    v-if="task.labels && task.labels.length > 6"
                    content-class="white pa-0 tooltip-opacity"
                    bottom
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" color="#757096" small
                        >mdi-dots-horizontal</v-icon
                      >
                    </template>
                    <span :class="isMac ? 'edited_scrollbar' : ''">
                      <v-card
                        max-width="300px"
                        max-height="200px"
                        class="y-scroll shadow"
                      >
                        <v-card-text>
                          <span
                            v-for="(label, index) in task.labels"
                            :key="index"
                          >
                            <v-chip
                              v-if="
                                getAllLabels.find((item) => {
                                  return item.id == label;
                                })
                              "
                              class="mr-1 mb-1 pointer"
                              label
                              style="'height:20px;border-radius:2px;max-width: 140px;font-size:12px'
                              "
                              x-small
                              :color="
                                getAllLabels.find((element) => {
                                  return element.id == label;
                                }).hexcode
                              "
                            >
                              <span
                                class="text-truncate d-inline-block white--text text-capitalize font-weight-bold"
                                style="max-width: 140px"
                              >
                                {{
                                  getAllLabels.find((element) => {
                                    return element.id == label;
                                  }).title
                                }}
                              </span>
                            </v-chip>
                          </span>
                        </v-card-text>
                      </v-card>
                    </span>
                  </v-tooltip>
                </div>
                <v-chip
                  v-if="task.just_dargged || task.just_added"
                  style="z-index: 1; position: absolute; top: -8px; width: 90px"
                  class="px-1"
                  x-small
                  color="#7ed492"
                  label
                  dark
                >
                  <v-avatar x-small class="mx-0 px-0" left>
                    <v-icon x-small>mdi-star</v-icon>
                  </v-avatar>
                  <span>
                    {{ task.just_dargged ? "Completed" : "New Task" }}
                  </span>
                </v-chip>
                <div style="max-width: 85%">
                  <!-- @click enable edit -->
                  <v-text-field
                    autocomplete="off"
                    autofocus
                    v-model="taskTitle"
                    dense
                    hide-details=""
                    color="#2a206a"
                    class="body-2 transparent my-0"
                    v-if="
                      selectedTask &&
                      selectedTask.task_id == task.task_id &&
                      (getSelectedCompany.role != 'employee' ||
                        task.assigned_by.id == getLoggedInUser.id)
                    "
                    @click.stop=""
                    @blur="selectedTask = null"
                    @keydown.esc="selectedTask = null"
                    @keydown.enter="editCardTitle(task)"
                  >
                    <template v-slot:append>
                      <v-icon @click.stop="editCardTitle(task)" color="green">
                        mdi-check
                      </v-icon>
                    </template>
                  </v-text-field>
                  <!-- Else show title text -->
                  <span
                    v-else
                    @click.stop="
                      setSelectedTask(task);
                      taskTitle = task.title;
                    "
                    class="pointer text-capitalize hyphenate font-weight-bold"
                  >
                    {{ task.title }}
                  </span>
                </div>
                <v-spacer></v-spacer>
                <!-- Assignee(s) -->
                <div class="d-flex align-center subtitle-2">
                  <div v-if="task.members" class="transparent pa-0">
                    <div class="d-flex align-center">
                      <div class="d-flex">
                        <!-- Assignee(s) Avatar -->
                        <ProfileImage
                          v-for="member in task.members.slice(0, 1)"
                          :key="member.email"
                          v-show="member.image ? ':src' : ':username'"
                          :src="member.image"
                          :userProfile="true"
                          inline
                          :username="member.name ? member.name : member.email"
                          :size="30"
                          :circular="true"
                          class="text-light d-inline ml-n3"
                          style="z-index: 2"
                        >
                        </ProfileImage>
                        <ProfileImage
                          v-if="task.members.length > 1"
                          inline
                          :username="`+${task.members.length - 1}`"
                          :size="30"
                          :userProfile="true"
                          :circular="true"
                          style="z-index: 1"
                          class="d-inline ml-n2"
                        >
                        </ProfileImage>
                      </div>
                    </div>
                  </div>
                </div>
              </v-card-title>
              <v-card-text class="dark-purple-text px-2">
                <div class="d-flex align-center">
                  <!-- Project -->
                  <div class="d-flex align-center blue-text text-small">
                    <v-icon
                      small
                      :color="task.project ? '#2B6AD8' : '#9E9E9E'"
                      class="mx-0 pa-0"
                      >mdi-circle-medium</v-icon
                    >
                    <span
                      :class="[
                        {
                          'grey--text': !task.project,
                        },
                      ]"
                      class="text-capitalize"
                    >
                      {{
                        task.project
                          ? task.project.name
                            ? task.project.name
                            : "No Project"
                          : "No Project"
                      }}</span
                    >
                  </div>
                  <v-divider vertical class="mx-2 py-2 my-0"></v-divider>
                  <!-- Assigned By -->
                  <div class="d-flex align-center text-small">
                    <img src="@/assets/images/user-icon.svg" alt="user-icon" />
                    <span
                      v-if="task.assigned_by"
                      style="max-width: 110px"
                      :class="[
                        {
                          'text-capitalize':
                            task.assigned_by && task.assigned_by.name,
                        },
                      ]"
                      class="mx-1 d-inline-block text-truncate grey-text"
                      >{{
                        task.assigned_by && task.assigned_by.name
                          ? task.assigned_by.name
                          : task.assigned_by.email
                      }}</span
                    >
                  </div>
                </div>
              </v-card-text>
              <v-card-actions>
                <!-- priority -->
                <v-menu nudge-bottom="25">
                  <template v-slot:activator="{ on }">
                    <div
                      v-on="on"
                      :id="`priority-${task.task_id}`"
                      :class="
                        task.assigned_by.id != getLoggedInUser.id &&
                        getSelectedCompany.role == 'employee'
                          ? ''
                          : 'pointer'
                      "
                    >
                      <v-icon color="red" v-if="task.priority == 'high'"
                        >mdi-chevron-triple-up</v-icon
                      >
                      <v-icon
                        color="orange"
                        v-else-if="task.priority == 'medium'"
                        >mdi-chevron-double-up</v-icon
                      >
                      <v-icon color="blue" v-else-if="task.priority == 'low'"
                        >mdi-chevron-double-up</v-icon
                      >

                      <v-icon v-else>mdi-chevron-double-up</v-icon>
                    </div>
                  </template>
                  <div
                    v-if="
                      !(
                        task.assigned_by.id != getLoggedInUser.id &&
                        getSelectedCompany.role == 'employee'
                      )
                    "
                    class="bg-white pointer py-2"
                  >
                    <div
                      v-for="(priority, i) in ['low', 'medium', 'high']"
                      :key="i"
                      :class="
                        task.priority && task.priority == priority
                          ? 'bg-selected'
                          : ''
                      "
                      class="body-2 dark-purple-text text-capitalize px-2 py-1"
                      @click="setPriority(task, priority)"
                    >
                      {{ priority }}
                    </div>
                  </div>
                </v-menu>
                <v-tooltip
                  :disabled="$vuetify.breakpoint.xs"
                  :activator="`#priority-${task.task_id}`"
                  content-class="arrow-up"
                  open-delay="120"
                  bottom
                  color="#DCD8F3"
                >
                  <span
                    class="text-capitalize font-weight-bold dark-purple-text"
                    >{{
                      task.priority ? task.priority + " priority" : "Priority"
                    }}</span
                  >
                </v-tooltip>
                <!-- Due date -->
                <v-chip
                  v-if="task.due_date"
                  class="due_date-chip-kb d-flex justify-center align-center"
                  label
                  small
                  light
                  :color="
                    task.due_date === getCurrentDate()
                      ? 'yellow-chip-color'
                      : task.due_date < getCurrentDate()
                      ? 'red-chip-color'
                      : 'green-chip-color'
                  "
                >
                  <span v-if="task.due_date === getCurrentDate()"> Today </span>
                  <span
                    v-else-if="
                      task.due_date === getTommorrowDate(task.due_date)
                    "
                  >
                    Tomorrow
                  </span>
                  <span v-else>
                    {{ getDueDate(task.due_date) }}
                  </span>
                </v-chip>
                <v-chip
                  v-if="!task.due_date"
                  class="due_date-chip-kb d-flex justify-center no-date align-center"
                  color="grey lighten-3"
                  label
                  small
                  light
                >
                  <span>No Due Date</span>
                </v-chip>

                <!-- Recurring -->
                <v-menu nudge-bottom="25">
                  <template v-slot:activator="{ on }">
                    <div
                      v-on="on"
                      :id="`repeat-${task.task_id}`"
                      :class="
                        task.assigned_by.id != getLoggedInUser.id &&
                        getSelectedCompany.role == 'employee'
                          ? ''
                          : 'pointer'
                      "
                    >
                      <v-icon
                        color="green"
                        class="ml-2"
                        size="16"
                        v-show="task.recurring_type"
                        >mdi-cached
                      </v-icon>
                    </div>
                  </template>
                  <div
                    v-if="
                      !(
                        task.assigned_by.id != getLoggedInUser.id &&
                        getSelectedCompany.role == 'employee'
                      )
                    "
                    class="bg-white pointer py-2"
                  >
                    <div
                      v-for="(recurring_type, i) in [
                        'daily',
                        'weekly',
                        'monthly',
                      ]"
                      :key="i"
                      :class="
                        task.recurring_type &&
                        task.recurring_type == recurring_type
                          ? 'bg-selected'
                          : ''
                      "
                      class="body-2 dark-purple-text text-capitalize px-2 py-1"
                      @click="
                        repeatTask(
                          task,
                          recurring_type == task.recurring_type
                            ? null
                            : recurring_type
                        )
                      "
                    >
                      {{ recurring_type }}
                    </div>
                  </div>
                </v-menu>
                <v-tooltip
                  :disabled="$vuetify.breakpoint.xs"
                  :activator="`#repeat-${task.task_id}`"
                  content-class="arrow-up"
                  open-delay="120"
                  bottom
                  color="#DCD8F3"
                >
                  <span
                    class="text-capitalize font-weight-bold dark-purple-text"
                    >{{
                      task.recurring_type
                        ? "Repeat " + task.recurring_type
                        : "Repeat task"
                    }}</span
                  >
                </v-tooltip>

                <!-- Time Spent -->
                <div class="caption d-flex align-center mx-2">
                  <v-icon color="#757096" small>mdi-clock-outline</v-icon>
                  <span
                    class="mx-1 text-capitalize font-weight-bold dusty-purple-text"
                  >
                    {{ getTotalDuration(task) }}</span
                  >
                </div>
                <v-spacer></v-spacer>
                <!-- Unflagged -->
                <v-icon
                  v-show="!task.is_flagged"
                  class="pointer"
                  color="#2a206a"
                  size="25"
                  @click.stop="
                    getSelectedCompany.role == 'employee' &&
                    task.assigned_by.id != getLoggedInUser.id
                      ? () => {}
                      : flagTaskKanban(task, true)
                  "
                  >mdi-bookmark-outline</v-icon
                >
                <!-- Flagged -->
                <v-icon
                  v-show="task.is_flagged"
                  @click.stop="
                    getSelectedCompany.role == 'employee' &&
                    task.assigned_by.id != getLoggedInUser.id
                      ? openSnackbar(true, 'Only assigner can flag task')
                      : flagTaskKanban(task, false)
                  "
                  class="pointer"
                  color="#D52A5D"
                  size="25"
                  >mdi-bookmark</v-icon
                >
                <!-- <v-btn
                  v-show="!task.is_flagged"
                  @click.stop="
                    getSelectedCompany.role == 'employee' &&
                    task.assigned_by.id != getLoggedInUser.id
                      ? () => {}
                      : flagTaskKanban(task, true)
                  "

                  color="#EDEBF9"
                  fab
                  depressed
                  height="30px"
                  width="30px"
                >
                  <v-icon size="20" color="#2a206a"
                    >mdi-bookmark-outline</v-icon
                  >
                </v-btn> -->

                <!-- <v-btn
                  v-show="task.is_flagged"
                  @click.stop="
                    getSelectedCompany.role == 'employee' &&
                    task.assigned_by.id != getLoggedInUser.id
                      ? openSnackbar(true, 'Only assigner can flag task')
                      : flagTaskKanban(task, false)
                  "
                  color="#EDEBF9"
                  fab
                  depressed
                  height="30px"
                  width="30px"
                >
                  <v-icon size="20" color="#D52A5D">mdi-bookmark</v-icon>
                </v-btn> -->
                <!-- comment icon -->
                <v-badge
                  :content="
                    parseInt(task.comments_count) < 100
                      ? task.comments_count
                      : '99+'
                  "
                  v-if="task.comments_count"
                  color="green"
                  :class="[
                    { 'mr-5': parseInt(task.comments_count) > 100 },
                    { 'mr-3': parseInt(task.comments_count) > 9 },
                    {
                      'style-badge': task.assigned_by.id == getLoggedInUser.id,
                    },
                  ]"
                  overlap
                  class="mx-3 mt-1"
                >
                  <img
                    :id="'comment-' + task.task_id"
                    @click.stop="
                      getPendingTimer && getPendingTimer.task_id == task.task_id
                        ? () => {}
                        : openComments(task)
                    "
                    height="26px"
                    width="28px"
                    class="dark-purple-text pointer"
                    :src="comment_icon"
                    alt="comment icon"
                  />
                </v-badge>
                <img
                  v-else
                  @click.stop="
                    getPendingTimer && getPendingTimer.task_id == task.task_id
                      ? () => {}
                      : openComments(task)
                  "
                  height="26px"
                  width="26px"
                  class="dark-purple-text pointer mx-2"
                  :src="comment_icon"
                  alt="comment icon"
                />
                <!-- Play/Pause Button -->
                <div class="d-flex align-center">
                  <img
                    v-if="
                      task.members.find((item) => {
                        return item.id == getLoggedInUser.id;
                      })
                    "
                    @click.stop="playTask(task), updateCompletedList(task)"
                    src="@/assets/images/play-circle-kanban.svg"
                    alt="play-icon"
                    height="28px"
                    width="28px"
                  />
                </div>
              </v-card-actions>
              <div>
                <v-tooltip
                  :disabled="$vuetify.breakpoint.xs"
                  color="#DCD8F3"
                  content-class="arrow-up"
                  bottom
                >
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      v-on="on"
                      v-bind="attrs"
                      class="serial mx-3 pointer"
                      v-if="task.sr_no"
                      @click.stop="copySerialNumber(task.sr_no)"
                    >
                      #{{ task.sr_no }}
                    </span>
                  </template>
                  <span
                    class="font-weight-bold dark-purple-text"
                    v-if="copiedSerialNumber != task.sr_no"
                    >Copy to clipboard</span
                  >
                  <span
                    class="font-weight-bold dark-purple-text"
                    v-else-if="copiedSerialNumber == task.sr_no"
                    >Copied</span
                  >
                </v-tooltip>
              </div>
            </v-card>
          </draggable>
          <div class="d-flex align-center justify-center pa-2">
            <p
              v-if="
                getCompletedPagination.last_page !=
                  getCompletedPagination.current_page &&
                getCompletedPagination.total > 10
              "
              dark
              block
              class="text-capitalize text-center pointer dusty-purple-text body-2 font-weight-bold"
              @click="loadMoreCompletedTask"
            >
              Load More
            </p>
            <v-progress-circular
              v-if="loading == 'loadCompleted'"
              color="#757096"
              :size="20"
              width="2"
              indeterminate
            ></v-progress-circular>
          </div>
        </div>
      </div>

      <!-- Sidebar for Editing Task Details -->
      <KanbanSidebar
        :assignMenu="details"
        :taskDetails="details"
        :drawer.sync="drawer"
      />
    </v-sheet>
    <!-- DIALOGS -->
    <!-- COMMENT DIALOG -->
    <template v-show="openCommentDialog">
      <CommentsDialogBox
        :openCommentDialog.sync="openCommentDialog"
        :taskDetails.sync="taskDetails"
        :getTaskComments="getTaskComments"
      />
    </template>
    <!-- Assign Task Dialog -->
    <AssignTaskModal :openAssignTaskDialog.sync="openAssignTaskDialog" />
    <!-- Assign Task Dialog -->
    <template v-if="openAddNewTaskKanbanDialog">
      <AddNewTaskModal
        :openAddNewTaskKanbanDialog.sync="openAddNewTaskKanbanDialog"
        :status="selectedAddTaskStatus"
      />
    </template>

    <!-- SNACKBAR -->
    <SnackbarNew
      :snackbar.sync="snackbar"
      :snackbarError="snackbarError"
      :snackbarMessage="snackbarMessage"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment";
import draggable from "vuedraggable";
import timer from "@/timer";
import { eventBus } from "@/main";
import { tasksMixin } from "@/mixins/tasksMixin";
import { dateHelpersMixin } from "@/mixins/dateHelpersMixin";
import { snackbarMixin } from "@/mixins/snackbarMixin";
import authAxios from "@/services/api.service.js";
export default {
  components: {
    draggable,
    ProfileImage: () => import("@/components/Includes/ProfileImage"),
    SnackbarNew: () => import("@/components/Includes/SnackbarNew"),
    CommentsDialogBox: () => import("../Comments/CommentsDialogBox_latest"),
    KanbanSidebar: () =>
      import("@/components/Tasks/KanbanView/KanbanComponents/KanbanSidebar"),
    AssignTaskModal: () => import("@/components/Tasks/AssignTaskModal"),
    AddNewTaskModal: () => import("@/components/Tasks/AddNewTaskModal"),
  },
  mixins: [dateHelpersMixin, snackbarMixin, tasksMixin],
  data() {
    return {
      showLabelName: true,
      pendingList: [],
      active: [],
      completed: [],
      pending: false,
      drawer: false,
      taskDetails: null,
      details: null,
      taskTitle: null,
      setTaskId: null,
      selectedTask: null,
      openCommentDialog: false,
      selectedAddTaskStatus: null,
      openAssignTaskDialog: false,
      openAddNewTaskKanbanDialog: false,
      loading: "",
    };
  },
  computed: {
    ...mapGetters([
      "getSidebarMini",
      "getPendingTasks",
      "getActiveTasks",
      "getKanbanTask",
      "getCompletedTasks",
      "getSelectedCompany",
      "getActiveTask",
      "getCompletedPagination",
      "getPendingPagination",
      "getActivePagination",
      "getPendingTimer",
      "getTimer",
      "getFetchKanbanLoader",
      "getView",
      "getProject",
      "getTaskToSendRecording",
      "getRecordingAvailable",
      "getAllLabels",
    ]),

    sortActiveTasks() {
      return this.active
        ? this.getActiveTask
          ? [this.getActiveTask].concat(
              this.active.filter((item) => {
                return item.task_id != this.getActiveTask.task_id;
              })
            )
          : this.active
        : [];
    },
    time() {
      return this.getActiveTask ? timer.format(this.getTimer) : "00:00:00";
    },
  },
  mounted() {
    eventBus.$on("updateKanbanTaskStatusEvent", this.updateKanbanTaskStatus);
    eventBus.$on("newTaskAssigned", this.openNewKanbanTaskComments);
    if (JSON.parse(localStorage.getItem("new-comment"))) {
      this.openComments(JSON.parse(localStorage.getItem("new-comment")));
      localStorage.removeItem("new-comment");
    }
    if (this.getRecordingAvailable) {
      this.taskDetails = this.getTaskToSendRecording;
    }
    eventBus.$on("new-comment", this.openCommentNotif);
    this.openCommentDialog = false;
    this.fetchCards();
  },
  methods: {
    // Update flag
    flagTaskKanban(task, option) {
      this.$store.commit("setTaskFlaggedKanban", {
        task_id: task.task_id,
        flagged: option,
      });
      let body = {
        company_id: this.getSelectedCompany.id,
        task_id: task.task_id,
        is_flagged: option,
      };
      this.$store.dispatch("updateTask", body);
    },
    repeatTask(task, recurring_type) {
      let oldData = {
        is_recurring: task.recurring_type ? true : false,
        recurring_type: task.recurring_type,
      };
      let data = {
        company_id: this.getSelectedCompany.id,
        task_id: task.task_id,
        is_recurring: recurring_type ? true : false,
        recurring_type: recurring_type,
      };
      this.$store.dispatch("updateTask", data).catch(() => {
        oldData.task = this.getKanbanTask(task.task_id);
        this.$store.commit("setUpdatedCard", oldData);

        // ERROR Snackbar
      });

      // Temporary update
      data.task = this.getKanbanTask(task.task_id);
      this.$store.commit("setUpdatedCard", data);
      this.dialogFlag = false;
    },
    fetchCards() {
      this.$store.commit("setFetchKanbanLoader", true);
      this.$store.dispatch("fetchKanbanTasks", "pending").then(() => {
        this.$store.commit("setFetchKanbanLoader", false);
      });
      this.$store.dispatch("fetchKanbanTasks", "active");
      this.$store.dispatch("fetchKanbanTasks", "completed");
    },
    // update status when marked completed/incomplete from comments dialog
    updateKanbanTaskStatus(params) {
      let { status, taskData } = params;
      let oldStatus = taskData.status;
      if (
        this.getActiveTask &&
        this.getActiveTask.task_id == taskData.task_id
      ) {
        this.openSnackbar(true, "Please stop, then complete");
        return;
      }
      let body = {
        company_id: this.getSelectedCompany.id,
        task_id: taskData.task_id,
        status,
      };
      this.$store.commit("setUpdatedCard", {
        task: this.getKanbanTask(taskData.task_id),
        status,
      });
      body.status == "completed"
        ? (body.completed_at = moment().format("YYYY-MM-DD HH:mm:ss"))
        : () => {};
      this.$store
        .dispatch("updateTaskCard", body)
        .then((res) => {
          this.$store.dispatch("fetchKanbanTasks", oldStatus);
          this.openSnackbar(false, res.data.error_msg);
        })
        .catch((error) => {
          if (error.response && error.response.status == 403) {
            this.openSnackbar(true, error.response.data.error_msg);
          }
        });
    },
    updateCompletedList(payload) {
      let task = {};
      Object.assign(task, this.getKanbanTask(payload.task_id));
      this.$store.commit("removeKanbanTask", {
        status: "completed",
        task_id: payload.task_id,
      });
      this.$store.commit("addKanbanTask", {
        status: "active",
        task,
        activeTask: this.getActiveTask,
      });
    },
    updateTaskCard(task, status) {
      this.$store.dispatch("updateTaskCard", {
        company_id: this.getSelectedCompany.id,
        task_id: task.task_id,
        status,
      });
    },
    updateTaskCardComplete(task, status) {
      if (this.getActiveTask && this.getActiveTask.task_id == task.task_id) {
        this.$store.dispatch("stopTask").then(() => {
          this.$store.dispatch("updateTaskCard", {
            company_id: this.getSelectedCompany.id,
            task_id: task.task_id,
            status,
            completed_at: moment().format("YYYY-MM-DD HH:mm:ss"),
          });
        });
      } else {
        this.$store.dispatch("updateTaskCard", {
          company_id: this.getSelectedCompany.id,
          task_id: task.task_id,
          status,
          completed_at: moment().format("YYYY-MM-DD HH:mm:ss"),
        });
      }
    },
    // Opens Right panel for editing
    editTask(task_details) {
      if (
        !(
          this.getPendingTimer &&
          this.getPendingTimer.task_id == task_details.task_id
        )
      ) {
        this.drawer = !this.drawer;
        this.details = task_details;
        eventBus.$emit("openSideBar");
      }
    },
    // Open Comment dialog for assigned task
    openNewKanbanTaskComments() {
      if (this.getView == "kanban") {
        if (this.getPendingTimer) {
          this.openComments(this.getPendingTimer);
        } else {
          this.openComments(this.getPendingTasks[0]);
        }
      }
    },
    openCommentNotif(payload) {
      this.openCommentDialog = false;
      if (
        payload.company_id != this.getSelectedCompany.id &&
        this.getCompany(payload.company_id)
      ) {
        this.$store.commit("setSelectedCompany", payload.company_id);
        localStorage.setItem("role", this.getCompany(payload.company_id).role);
        this.fetchCards();
      }
      let task = this.getKanbanTask(payload.task_id);
      if (task) {
        task.comments_count = 0;
        this.openComments(task);
      } else {
        authAxios
          .post("task/get-detail", JSON.parse(localStorage.getItem("token")), {
            task_id: payload.task_id,
            company_id: this.getSelectedCompany.id,
          })
          .then((res) => {
            let task = res.data.data;
            task.comments_count = 0;
            this.openComments(task);
          });
      }
    },
    // Opens Comment Dialog
    openComments(task) {
      this.taskDetails = task;
      task.comments_count = 0;
      this.openCommentDialog = true;
    },
    // Disable drop if task is not self assigned or logged in user is not a task member
    checkMovePending: function (evt) {
      // If empty, disable drop
      if (evt.dragged.id == "pending-empty-col") return false;
      // Else
      let addedTask = evt.draggedContext.element;
      let isTaskMember = addedTask.members.find((item) => {
        return item.id == this.getLoggedInUser.id;
      });
      // Task assigned by others cannot be updated by assignee
      if (
        this.getSelectedCompany.role == "employee" &&
        addedTask.assigned_by.id != this.getLoggedInUser.id
      ) {
        if (evt.to.id == "completed") {
          this.openSnackbar(true, "Only creator can update metadata of task");
          return false;
        } else return true;
      }
      // Admin cases
      if (evt.to.id == "active") {
        if (!isTaskMember) {
          this.openSnackbar(true, "Task is not assigned to you");
          return false;
        } else this.closeSnackbar();
        return true;
      } else if (evt.to.id == "completed") {
        this.closeSnackbar();
        return true;
      }
      this.closeSnackbar();
      return true;
    },
    // Disable drop if task status is being reversed to pending state
    checkMove: function (evt) {
      // If empty, disable drop
      if (evt.dragged.id == "active-empty-col") return false;
      // Else
      let addedTask = evt.draggedContext.element;
      // Task assigned by others cannot be updated by assignee
      if (
        this.getSelectedCompany.role == "employee" &&
        addedTask.assigned_by.id != this.getLoggedInUser.id
      ) {
        this.openSnackbar(true, "Only creator can update metadata of task");
        return false;
      }
      // Cannot move active task to pending
      if (evt.to.id == "pending") {
        this.openSnackbar(
          true,
          "Task cannot be moved to pending state because of existing time entries"
        );
        return false;
      }
      if (evt.to.id == "completed") {
        this.closeSnackbar();
        return true;
      }
    },

    checkMoveCompleted: function (evt) {
      // If empty, disable drop
      if (evt.dragged.id == "completed-empty-col") return false;
      // Else
      let addedTask = evt.draggedContext.element;
      let hasTimeSpent = addedTask.members
        .map((item) => {
          return item.duration;
        })
        .reduce((a, b) => {
          return a + b;
        });
      let isTaskMember = addedTask.members.find((item) => {
        return item.id == this.getLoggedInUser.id;
      });
      // Task assigned by others cannot be updated by assignee
      if (
        this.getSelectedCompany.role == "employee" &&
        addedTask.assigned_by.id != this.getLoggedInUser.id
      ) {
        this.openSnackbar(true, "Only creator can update metadata of task");
        return false;
      }
      // Cannot revert task status to pending
      else if (evt.to.id == "pending") {
        if (hasTimeSpent) {
          this.openSnackbar(
            true,
            "Task cannot be moved to pending state because of existing time entries"
          );
          return false;
        } else this.closeSnackbar();
        return true;
      } else if (evt.to.id == "active") {
        // Employee case
        if (this.getSelectedCompany.role == "employee") {
          // Future task
          if (addedTask.self_assigned && !hasTimeSpent) {
            this.openSnackbar(
              true,
              "Task not started yet. Please move to pending"
            );
            return false;
          }
          if (addedTask.self_assigned && hasTimeSpent) {
            this.closeSnackbar();
            return true;
          }
          return false;
        }
        // Admin case
        else {
          // Someone else's task not started yet (admin)
          if (!isTaskMember && !hasTimeSpent) {
            this.openSnackbar(
              true,
              "Assignee has not started working. Please move to pending"
            );
            return false;
          }
          if (isTaskMember && !hasTimeSpent) {
            this.openSnackbar(
              true,
              "Task not started yet. Please move to pending"
            );
            return false;
          } else this.closeSnackbar();
          return true;
        }
      }
    },
    // Functions for updating task status
    setSelectedTask(task) {
      this.selectedTask = task;
    },
    // moves task to active array
    addActiveTask(evt) {
      if (evt.added) {
        let addedTask = evt.added.element;
        if (addedTask && addedTask.status == "pending")
          this.playTask(addedTask);
        else this.updateTaskCard(addedTask, "active");
      }
    },
    // moves task to completed array
    addCompletedTask(evt) {
      if (evt.added) {
        let addedTask = evt.added.element;
        this.updateTaskCardComplete(addedTask, "completed");
      }
    },
    // moves task to pending array
    addPendingTask(evt) {
      if (evt.added) {
        let addedTask = evt.added.element;
        this.updateTaskCardComplete(addedTask, "pending");
      }
    },
    // Starts a task
    playTask(task) {
      if (this.pending) return;
      this.pending = "play";
      if (this.getActiveTask) {
        if (!this.getActiveTask.title) {
          this.openSnackbar(true, "Cannot stop current task without title");
          return;
        }
        this.setTaskId = task.task_id;
        this.updateStatusLoader = true;
        this.$store
          .dispatch("stopTask")
          .then(() => {
            this.$store
              .dispatch("startTask", task)
              .then(() => {
                this.updateStatusLoader = false;
                this.pending = false;
                this.$store.commit("setUpdatedCard", {
                  task: this.getKanbanTask(task.task_id),
                  status: "active",
                });
              })
              .catch(() => {
                this.pending = false;
              });
          })
          .catch(() => {
            this.pending = false;
          });
      } else {
        this.setTaskId = task.task_id;
        this.updateStatusLoader = true;
        this.$store
          .dispatch("startTask", task)
          .then(() => {
            this.pending = false;
            this.updateStatusLoader = false;
            this.$store.commit("setUpdatedCard", {
              task: this.getKanbanTask(task.task_id),
              status: "active",
            });
          })
          .catch(() => {
            this.pending = false;
          });
      }
    },
    loadMoreCompletedTask() {
      this.loading = "loadCompleted";
      let oldCompletedTasks = this.completed;
      this.$store.commit("updateKanbanPagination", {
        status: "completed",
        page: this.getCompletedPagination.current_page + 1,
      });
      this.$store.dispatch("fetchKanbanTasks", "completed").then(() => {
        this.$store.commit("setLoadedKanbanTasks", {
          status: "completed",
          tasks: oldCompletedTasks,
        });
        this.loading = "";
      });
    },
    loadMorePendingTasks() {
      this.loading = "loadPending";
      let oldPendingTasks = this.pendingList;
      this.$store.commit("updateKanbanPagination", {
        status: "pending",
        page: this.getPendingPagination.current_page + 1,
      });
      this.$store.dispatch("fetchKanbanTasks", "pending").then(() => {
        this.$store.commit("setLoadedKanbanTasks", {
          status: "pending",
          tasks: oldPendingTasks,
        });
        this.loading = "";
      });
    },
    loadMoreActiveTasks() {
      this.loading = "loadActive";
      let oldActiveTasks = this.active;
      this.$store.commit("updateKanbanPagination", {
        status: "active",
        page: this.getActivePagination.current_page + 1,
      });
      this.$store.dispatch("fetchKanbanTasks", "active").then(() => {
        this.$store.commit("setLoadedKanbanTasks", {
          status: "active",
          tasks: oldActiveTasks,
        });
        this.loading = "";
      });
    },
    // Remove task from pending and add in active array
    updatePendingList(payload) {
      let task = {};
      Object.assign(task, this.getKanbanTask(payload.task_id));
      this.$store.commit("removeKanbanTask", {
        status: "pending",
        task_id: payload.task_id,
      });
      this.$store.commit("addKanbanTask", {
        status: "active",
        task,
        activeTask: this.getActiveTask,
      });
      this.playTask(task);
    },
    // Stop a running task from list
    stopTask() {
      if (this.getActiveTask) this.$emit("stopTask");
    },
    // Edit task title from list
    editCardTitle(data) {
      let trimTitle = this.taskTitle.trim();
      let oldTitle = this.getKanbanTask(data.task_id).title;
      if (trimTitle.length > 0) {
        let body = {
          company_id: this.getSelectedCompany.id,
          task_id: data.task_id,
          title: trimTitle,
        };
        if (this.getActiveTask && this.getActiveTask.task_id == data.task_id)
          eventBus.$emit("titleUpdated", trimTitle);
        this.$store.commit("setUpdatedCard", {
          project: data.project,
          task: this.getKanbanTask(data.task_id),
          title: trimTitle,
        });
        this.$store
          .dispatch("editTaskCardInfo", body)
          .then((res) => {
            this.search = null;
            this.openSnackbar(false, res.data.error_msg);
          })
          .catch((error) => {
            this.$store.commit("setUpdatedCard", {
              project: data.project,
              task: this.getKanbanTask(data.task_id),
              title: oldTitle,
            });
            if (error.response.status == 422) {
              if (error.response.data.errors.title) {
                this.openSnackbar(true, error.response.data.errors.title[0]);
              }
            }
            if (error.response.status == 403) {
              this.openSnackbar(true, error.response.data.error_msg);
            }
          });
        this.selectedTask = null;
        this.taskTitle = null;
      }
    },
  },
  watch: {
    getRecordingAvailable(value) {
      if (value) this.openComments(this.getTaskToSendRecording);
    },
    getActiveTasks(value) {
      this.active = value;
    },
    getPendingTasks(value) {
      this.pendingList = value;
    },
    getCompletedTasks(value) {
      this.completed = value;
    },
    // Update comments dialog details if pending task has resolved
    getPendingTimer(value, oldValue) {
      if (!value && this.openCommentDialog && this.getView == "kanban") {
        if (
          oldValue.members.find((item) => {
            return item.id == this.getLoggedInUser.id;
          })
        ) {
          if (oldValue.status == "pending")
            this.taskDetails = this.getPendingTasks[0];
          else this.taskDetails = this.getActiveTasks[0];
        } else {
          this.taskDetails = this.getPendingTasks[0];
        }
      }
    },
    openCommentDialog(value) {
      if (!value) {
        this.taskDetails = null;
      }
    },
  },
  beforeDestroy() {
    eventBus.$off("newTaskAssigned");
    eventBus.$off("updateKanbanTaskStatusEvent");
  },
};
</script>
<style scoped>
.card-css {
  background-color: #f8f7fd !important;
}
/* purple column */
.column-height {
  height: calc(100vh - 200px) !important;
  min-height: calc(100vh - 200px) !important;
  overflow-y: hidden;
}
/* .col-width {
  width: 400px;
} */
/* drag n drop area */
.kanban-col-height {
  height: calc(100vh - 290px) !important;
  min-height: calc(100vh - 290px) !important;
}
div.sticky {
  position: -webkit-sticky;
  position: sticky;
  top: 0;
}

.card-skeleton-border {
  border: 1px solid rgba(209, 209, 209, 0.568);
}
.pending-col-top-border {
  border-top: 5px solid #ffb74d;
}
.active-col-top-border {
  border-top: 5px solid #65b6f7;
}
.completed-col-top-border {
  border-top: 5px solid #7ed492;
}
.blue-text {
  color: #2b6ad8 !important ;
}
.grey-text {
  color: #6f7490 !important;
}
.text-small {
  font-size: 12px !important;
}
.dark-red-text {
  color: #d52a5d;
}

.default-width {
  max-width: calc(100vw - 34px);
}
.reduced-width {
  max-width: calc(100vw - 300px);
}
.col-width-1 {
  /* width: 434px; */
  width: 40vw;
  min-width: 434px;
}
.col-width-2 {
  width: 35vw;
  min-width: 360px;
}
.col-width-sm {
  min-width: 78vw;
}
</style>
